// Minimal QR Code encoder (byte mode, automatic version/EC selection).
// Vendored so NerdTools has zero runtime CDN dependency (project is a
// client-side SPA). Implements QR Model 2, EC level M, versions 1-10.
// Returns a boolean module matrix. Covered by tests/qrtest.mjs which
// checks structural invariants (size, finder patterns, quiet zone).

// ---- Galois field GF(256) for Reed-Solomon ----
const EXP = new Array(512);
const LOG = new Array(256);
(function initGF() {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255];
})();
const gmul = (a, b) => (a === 0 || b === 0 ? 0 : EXP[LOG[a] + LOG[b]]);

function rsGenPoly(n) {
  let poly = [1];
  for (let i = 0; i < n; i++) {
    const next = new Array(poly.length + 1).fill(0);
    for (let j = 0; j < poly.length; j++) {
      next[j] ^= poly[j];
      next[j + 1] ^= gmul(poly[j], EXP[i]);
    }
    poly = next;
  }
  return poly;
}
function rsEncode(data, ecLen) {
  const gen = rsGenPoly(ecLen);
  const res = data.concat(new Array(ecLen).fill(0));
  for (let i = 0; i < data.length; i++) {
    const coef = res[i];
    if (coef !== 0) for (let j = 0; j < gen.length; j++) res[i + j] ^= gmul(gen[j], coef);
  }
  return res.slice(data.length);
}

// ---- Capacity tables (EC level M, byte mode), versions 1-10 ----
// [version]: { totalCodewords, ecPerBlock, group1Blocks, g1DataCW,
//              group2Blocks, g2DataCW, byteCapacity }
export const CAP_M = {
  1: { ec: 10, g1: 1, g1cw: 16, g2: 0, g2cw: 0, cap: 14 },
  2: { ec: 16, g1: 1, g1cw: 28, g2: 0, g2cw: 0, cap: 26 },
  3: { ec: 26, g1: 1, g1cw: 44, g2: 0, g2cw: 0, cap: 42 },
  4: { ec: 18, g1: 2, g1cw: 32, g2: 0, g2cw: 0, cap: 62 },
  5: { ec: 24, g1: 2, g1cw: 43, g2: 0, g2cw: 0, cap: 84 },
  6: { ec: 16, g1: 4, g1cw: 27, g2: 0, g2cw: 0, cap: 106 },
  7: { ec: 18, g1: 4, g1cw: 31, g2: 0, g2cw: 0, cap: 122 },
  8: { ec: 22, g1: 2, g1cw: 38, g2: 2, g2cw: 39, cap: 152 },
  9: { ec: 22, g1: 3, g1cw: 36, g2: 2, g2cw: 37, cap: 180 },
  // v10-M per-block EC is 26 (28 is the v10-H value — table typo that
  // overflowed the 346-codeword symbol by 10 and made v10 unscannable;
  // confirmed by two independent adversarial reviews + a full-table
  // codeword-arithmetic audit, real-decoder verified after the fix).
  10: { ec: 26, g1: 4, g1cw: 43, g2: 1, g2cw: 44, cap: 213 },
};

// Full QR versions 1-10, EC level M, byte mode, fixed mask 0, with
// BCH(18,6) version-information blocks for v>=7. Real-decoder (jsQR)
// round-trip verified for every version 1-10 INCLUDING the v10
// capacity boundary, after fixing a CAP_M[10] EC-count typo
// (ec:28→26) that two independent adversarial reviews + a full-table
// codeword audit confirmed. Over the v10 capacity (~213 bytes, EC-M),
// reject with an actionable error rather than emit an unscannable code.
const MAX_VERSION = 10;
function chooseVersion(byteLen) {
  for (let v = 1; v <= MAX_VERSION; v++) if (byteLen <= CAP_M[v].cap) return v;
  throw new Error(
    `Text is too long (${byteLen} bytes). The maximum is ${CAP_M[MAX_VERSION].cap} bytes — try a shorter URL or use a link shortener.`
  );
}

function toBytes(str) {
  return Array.from(new TextEncoder().encode(str));
}

function buildDataCodewords(str, v) {
  const bytes = toBytes(str);
  const info = CAP_M[v];
  const totalDataCW = info.g1 * info.g1cw + info.g2 * info.g2cw;
  let bits = [];
  const push = (val, len) => { for (let i = len - 1; i >= 0; i--) bits.push((val >> i) & 1); };
  push(0b0100, 4); // byte mode
  push(bytes.length, v <= 9 ? 8 : 16); // char count indicator
  for (const b of bytes) push(b, 8);
  // terminator
  const cap = totalDataCW * 8;
  for (let i = 0; i < 4 && bits.length < cap; i++) bits.push(0);
  while (bits.length % 8 !== 0) bits.push(0);
  const cw = [];
  for (let i = 0; i < bits.length; i += 8) {
    let b = 0;
    for (let j = 0; j < 8; j++) b = (b << 1) | bits[i + j];
    cw.push(b);
  }
  // pad bytes
  const pads = [0xec, 0x11];
  let pi = 0;
  while (cw.length < totalDataCW) cw.push(pads[pi++ % 2]);
  return cw;
}

function interleave(dataCW, v) {
  const info = CAP_M[v];
  const blocks = [];
  let idx = 0;
  for (let i = 0; i < info.g1; i++) { blocks.push(dataCW.slice(idx, idx + info.g1cw)); idx += info.g1cw; }
  for (let i = 0; i < info.g2; i++) { blocks.push(dataCW.slice(idx, idx + info.g2cw)); idx += info.g2cw; }
  const ecBlocks = blocks.map((b) => rsEncode(b, info.ec));
  const maxData = Math.max(...blocks.map((b) => b.length));
  const out = [];
  for (let i = 0; i < maxData; i++) for (const b of blocks) if (i < b.length) out.push(b[i]);
  for (let i = 0; i < info.ec; i++) for (const e of ecBlocks) out.push(e[i]);
  return out;
}

// ---- Version information: BCH(18,6) for versions 7-40 ----
// QR spec: v>=7 carries an 18-bit version block = 6 data bits (version)
// + 12 BCH error-correction bits, generator polynomial 0x1F25.
function bchVersion(v) {
  let d = v << 12;
  const g = 0x1f25; // x^12+x^11+x^10+x^9+x^8+x^5+x^2+1
  for (let i = 17; i >= 12; i--) {
    if ((d >> i) & 1) d ^= g << (i - 12);
  }
  return (v << 12) | (d & 0xfff); // 18-bit value, MSB-first = bit17..bit0
}

// ---- Matrix placement ----
function buildMatrix(finalCW, v) {
  const size = 17 + v * 4;
  const m = Array.from({ length: size }, () => new Array(size).fill(null));
  const reserve = (r, c, val) => { if (r >= 0 && r < size && c >= 0 && c < size) m[r][c] = val; };

  // finder + separators
  const finder = (R, C) => {
    for (let r = -1; r <= 7; r++)
      for (let c = -1; c <= 7; c++) {
        const inRange = r >= 0 && r <= 6 && c >= 0 && c <= 6;
        const on = inRange && (r === 0 || r === 6 || c === 0 || c === 6 || (r >= 2 && r <= 4 && c >= 2 && c <= 4));
        reserve(R + r, C + c, on ? 1 : 0);
      }
  };
  finder(0, 0); finder(0, size - 7); finder(size - 7, 0);

  // timing
  for (let i = 8; i < size - 8; i++) {
    if (m[6][i] === null) m[6][i] = i % 2 === 0 ? 1 : 0;
    if (m[i][6] === null) m[i][6] = i % 2 === 0 ? 1 : 0;
  }
  // dark module
  m[size - 8][8] = 1;

  // alignment pattern (single, versions 2-6 have one centered; 7-10 more)
  const alignPos = {
    1: [], 2: [6, 18], 3: [6, 22], 4: [6, 26], 5: [6, 30], 6: [6, 34],
    7: [6, 22, 38], 8: [6, 24, 42], 9: [6, 26, 46], 10: [6, 28, 50],
  }[v];
  for (const ar of alignPos) for (const ac of alignPos) {
    if ((ar <= 7 && ac <= 7) || (ar <= 7 && ac >= size - 8) || (ar >= size - 8 && ac <= 7)) continue;
    for (let r = -2; r <= 2; r++)
      for (let c = -2; c <= 2; c++) {
        const on = Math.max(Math.abs(r), Math.abs(c)) !== 1;
        reserve(ar + r, ac + c, on ? 1 : 0);
      }
  }

  // reserve format areas (filled after masking)
  for (let i = 0; i < 9; i++) { if (m[8][i] === null) m[8][i] = 0; if (m[i][8] === null) m[i][8] = 0; }
  for (let i = 0; i < 8; i++) { if (m[8][size - 1 - i] === null) m[8][size - 1 - i] = 0; if (m[size - 1 - i][8] === null) m[size - 1 - i][8] = 0; }

  // reserve version-info area for v>=7 (filled after data placement):
  // a 6x3 block to the LEFT of the top-right finder, and its transpose
  // ABOVE the bottom-left finder. Reserve so data does not land here.
  if (v >= 7) {
    for (let i = 0; i < 6; i++)
      for (let j = 0; j < 3; j++) {
        m[i][size - 11 + j] = 0;       // top-right block
        m[size - 11 + j][i] = 0;       // bottom-left block (transpose)
      }
  }

  // place data with mask 0 ((r+c)%2==0)
  let bitIdx = 0;
  const allBits = [];
  for (const cw of finalCW) for (let b = 7; b >= 0; b--) allBits.push((cw >> b) & 1);
  let up = true;
  for (let col = size - 1; col > 0; col -= 2) {
    if (col === 6) col--;
    for (let i = 0; i < size; i++) {
      const row = up ? size - 1 - i : i;
      for (let dc = 0; dc < 2; dc++) {
        const c = col - dc;
        if (m[row][c] === null) {
          let bit = bitIdx < allBits.length ? allBits[bitIdx++] : 0;
          if ((row + c) % 2 === 0) bit ^= 1; // mask 0
          m[row][c] = bit;
        }
      }
    }
    up = !up;
  }

  // format info for EC level M, mask 0 = bits 0b101010000010010 (per spec)
  const fmt = [1, 0, 1, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 1, 0];
  for (let i = 0; i <= 5; i++) { m[8][i] = fmt[i]; m[i][8] = fmt[14 - i]; }
  m[8][7] = fmt[6]; m[8][8] = fmt[7]; m[7][8] = fmt[8];
  for (let i = 0; i <= 5; i++) m[size - 1 - i][8] = fmt[i];
  m[size - 7][8] = fmt[6];
  for (let i = 0; i < 8; i++) m[8][size - 1 - i] = fmt[14 - i];

  // version information (v>=7): 18 bits, bit 0 = LSB. Per ISO/IEC 18004,
  // bit index k maps to module (k//3, size-11 + k%3) for the top-right
  // block and the transposed coordinate for the bottom-left block.
  if (v >= 7) {
    const vinfo = bchVersion(v);
    for (let k = 0; k < 18; k++) {
      const bit = (vinfo >> k) & 1;
      const r = Math.floor(k / 3);
      const c = size - 11 + (k % 3);
      m[r][c] = bit;       // top-right
      m[c][r] = bit;       // bottom-left (transpose)
    }
  }

  return m.map((row) => row.map((v) => v === 1));
}

export function generateQR(text) {
  if (!text) throw new Error("Empty text");
  const bytes = toBytes(text);
  const v = chooseVersion(bytes.length);
  const dataCW = buildDataCodewords(text, v);
  const finalCW = interleave(dataCW, v);
  const matrix = buildMatrix(finalCW, v);
  return { version: v, size: matrix.length, matrix };
}

